import React, { useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  IconButton,
  RadioGroup,
  FormControlLabel,
  Radio,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import dayjs from 'dayjs';
import { X } from 'lucide-react';
import { Task, TaskDueDate } from '../../types';
import { users } from '../../data/sampleData';

interface TaskDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (task: Partial<Task>) => void;
  task?: Task;
  stageId: string;
  hideAssignment?: boolean;
  isTemplate?: boolean;
}

const taskFormTypes = [
  { id: 'medical-records', name: 'Medical Record Review' },
  { id: 'expert-witness', name: 'Expert Witness' },
  { id: 'interrogatories', name: 'Interrogatories' },
  { id: 'default', name: 'Standard Task' },
];

export const TaskDialog: React.FC<TaskDialogProps> = ({
  open,
  onClose,
  onSave,
  task,
  stageId,
  hideAssignment,
  isTemplate = false,
}) => {
  const [formData, setFormData] = React.useState<Partial<Task> & { 
    formType?: string;
    dueDateType?: 'relative' | 'absolute';
    relativeDays?: number;
    relativeUnit?: 'days' | 'weeks' | 'months';
  }>({
    title: '',
    description: '',
    assignedTo: '',
    dueDate: new Date().toISOString(),
    dueDateType: isTemplate ? 'relative' : 'absolute',
    relativeDays: 7,
    relativeUnit: 'days',
    status: 'pending',
    priority: 'medium',
    stageId,
    formType: 'default',
  });

  useEffect(() => {
    if (open) {
      let dueDateData = {
        dueDateType: isTemplate ? 'relative' : 'absolute',
        relativeDays: 7,
        relativeUnit: 'days',
        dueDate: new Date().toISOString(),
      };

      if (task?.dueDate && typeof task.dueDate === 'object') {
        dueDateData = {
          dueDateType: 'relative',
          relativeDays: task.dueDate.value,
          relativeUnit: task.dueDate.unit,
          dueDate: new Date().toISOString(),
        };
      } else if (task?.dueDate) {
        dueDateData = {
          dueDateType: 'absolute',
          relativeDays: 7,
          relativeUnit: 'days',
          dueDate: task.dueDate,
        };
      }

      setFormData({
        title: task?.title || '',
        description: task?.description || '',
        assignedTo: task?.assignedTo || '',
        status: task?.status || 'pending',
        priority: task?.priority || 'medium',
        stageId: task?.stageId || stageId,
        formType: getFormType(task?.title),
        ...dueDateData,
      });
    }
  }, [open, task, stageId, isTemplate]);

  const getFormType = (title?: string) => {
    if (!title) return 'default';
    if (title === 'Review Medical Records') return 'medical-records';
    if (title === 'Schedule Expert Witness') return 'expert-witness';
    if (title === 'Draft Interrogatories') return 'interrogatories';
    return 'default';
  };

  const handleChange = (field: keyof typeof formData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));

    if (field === 'formType' && !task) {
      const titles: Record<string, string> = {
        'medical-records': 'Review Medical Records',
        'expert-witness': 'Schedule Expert Witness',
        'interrogatories': 'Draft Interrogatories',
      };
      if (titles[value]) {
        setFormData(prev => ({ ...prev, title: titles[value] }));
      }
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const { formType, dueDateType, relativeDays, relativeUnit, ...taskData } = formData;

    // Convert due date based on type
    let dueDate: string | TaskDueDate = taskData.dueDate as string;
    if (dueDateType === 'relative') {
      dueDate = {
        type: 'relative',
        value: relativeDays || 7,
        unit: relativeUnit || 'days',
      };
    }

    onSave({ ...taskData, dueDate });
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          height: 'auto',
          maxHeight: '90vh',
        },
      }}
    >
      <form onSubmit={handleSubmit}>
        <DialogTitle sx={{ m: 0, p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6" component="div">
            {task ? 'Edit Task' : 'New Task'}
          </Typography>
          <IconButton
            aria-label="close"
            onClick={onClose}
            sx={{
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <X size={20} />
          </IconButton>
        </DialogTitle>
        <DialogContent 
          dividers
          sx={{
            p: 3,
            display: 'flex',
            flexDirection: 'column',
            gap: 2.5,
          }}
        >
          <FormControl fullWidth>
            <InputLabel>Task Type</InputLabel>
            <Select
              value={formData.formType}
              label="Task Type"
              onChange={(e) => handleChange('formType', e.target.value)}
              disabled={Boolean(task)}
            >
              {taskFormTypes.map((type) => (
                <MenuItem key={type.id} value={type.id}>
                  {type.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
            
          <TextField
            label="Title"
            fullWidth
            required
            value={formData.title}
            onChange={(e) => handleChange('title', e.target.value)}
            disabled={formData.formType !== 'default'}
          />
          
          <TextField
            label="Description"
            fullWidth
            multiline
            rows={3}
            value={formData.description}
            onChange={(e) => handleChange('description', e.target.value)}
          />

          {!hideAssignment && (
            <FormControl fullWidth>
              <InputLabel>Assigned To</InputLabel>
              <Select
                value={formData.assignedTo}
                label="Assigned To"
                onChange={(e) => handleChange('assignedTo', e.target.value)}
                required
              >
                {users.map((user) => (
                  <MenuItem key={user.id} value={user.id}>
                    {user.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Due Date
            </Typography>
            <RadioGroup
              value={formData.dueDateType}
              onChange={(e) => handleChange('dueDateType', e.target.value)}
              sx={{ mb: 2 }}
            >
              <FormControlLabel 
                value="relative" 
                control={<Radio />} 
                label="Relative to case creation"
              />
              <FormControlLabel 
                value="absolute" 
                control={<Radio />} 
                label="Specific date"
              />
            </RadioGroup>

            {formData.dueDateType === 'relative' ? (
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                <TextField
                  label="Time"
                  type="number"
                  value={formData.relativeDays}
                  onChange={(e) => handleChange('relativeDays', parseInt(e.target.value))}
                  inputProps={{ min: 1 }}
                  sx={{ width: 100 }}
                />
                <FormControl sx={{ minWidth: 120 }}>
                  <InputLabel>Unit</InputLabel>
                  <Select
                    value={formData.relativeUnit}
                    label="Unit"
                    onChange={(e) => handleChange('relativeUnit', e.target.value)}
                  >
                    <MenuItem value="days">Days</MenuItem>
                    <MenuItem value="weeks">Weeks</MenuItem>
                    <MenuItem value="months">Months</MenuItem>
                  </Select>
                </FormControl>
              </Box>
            ) : (
              <DatePicker
                label="Due Date"
                value={dayjs(formData.dueDate)}
                onChange={(date) => handleChange('dueDate', date?.toISOString())}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    required: true,
                  }
                }}
              />
            )}
          </Box>

          <FormControl fullWidth>
            <InputLabel>Priority</InputLabel>
            <Select
              value={formData.priority}
              label="Priority"
              onChange={(e) => handleChange('priority', e.target.value)}
              required
            >
              <MenuItem value="low">Low</MenuItem>
              <MenuItem value="medium">Medium</MenuItem>
              <MenuItem value="high">High</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={onClose} color="inherit">
            Cancel
          </Button>
          <Button type="submit" variant="contained">
            {task ? 'Save Changes' : 'Create Task'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};